/**
 * DesignReportView - デザインファイル準拠のレポート表示コンポーネント
 * assets/design/company_wide/your-report.html を再現
 */
import React from 'react';
import { DesignRadarChart } from './DesignRadarChart';
import './DesignReportView.scss';

export interface ReportUserProfile {
  name: string;
  initials: string;
  date: string;
  gender?: string;
  company?: string;
  team?: string;
  position?: string;
}

export interface ReportScores {
  A: number;
  B: number;
  C: number;
  D: number;
}

export interface ReportContent {
  /** GBタイプ名 */
  thinkingType: string;
  /** 気質タイプ */
  temperamentType: string;
  /** 思考スタイルテーマ */
  thoughtTheme: string;
  /** 思考スタイル説明 */
  thoughtDescription: string;
  /** 行動スタイルテーマ */
  actionTheme: string;
  /** 行動スタイル説明 */
  actionDescription: string;
}

export interface DesignReportViewProps {
  /** ユーザープロフィール */
  profile: ReportUserProfile;
  /** スコア（A, B, C, D） */
  scores: ReportScores;
  /** レポート内容（オプション） */
  content?: ReportContent;
  /** 比較用スコア（同業種平均など） */
  compareScores?: ReportScores;
  /** ローディング状態 */
  isLoading?: boolean;
}

/**
 * デザインファイル準拠のレポートビュー
 */
export const DesignReportView: React.FC<DesignReportViewProps> = ({
  profile,
  scores,
  content,
  // compareScores は2ページ目で使用予定
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  compareScores: _compareScores,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="design-report-loading">
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="c-report-single">
      {/* ユーザーラベル */}
      <h2 className="c-report-single_label">
        <span className="c-report-single_label_icon">{profile.initials}</span>
        {profile.name}
      </h2>

      <div className="c-report-single_inner">
        <ul className="c-report-single_list">
          {/* ページ1: メインレポート */}
          <li className="c-report-single_list_item">
            <div className="c-report-single_list_item_inner">
              {/* ヘッダー */}
              <header className="c-report-single_header">
                <hgroup className="c-report-single_header_hgroup">
                  <p className="c-report-single_header_hgroup_logo">
                    <img src="/assets/images/logo.svg" alt="GB人財" />
                  </p>
                  <h3 className="c-report-single_header_hgroup_ttl">GBタイプ診断結果</h3>
                </hgroup>

                <ul className="c-report-single_header_profile">
                  <li className="c-report-single_header_profile_item">{profile.date}</li>
                  {profile.gender && (
                    <li className="c-report-single_header_profile_item">{profile.gender}</li>
                  )}
                  {profile.company && (
                    <li className="c-report-single_header_profile_item">{profile.company}</li>
                  )}
                  {profile.team && (
                    <li className="c-report-single_header_profile_item">{profile.team}</li>
                  )}
                  {profile.position && (
                    <li className="c-report-single_header_profile_item">{profile.position}</li>
                  )}
                  <li className="c-report-single_header_profile_item">{profile.name}</li>
                </ul>
              </header>

              {/* 結果セクション1 */}
              <div className="c-report-single_result-1">
                <div className="c-report-single_result-1_main">
                  <hgroup className="c-report-single_result-1_main_hgroup">
                    <p className="c-report-single_result-1_main_hgroup_txt">あなたのGBタイプは</p>
                    <h4 className="c-report-single_result-1_main_hgroup_ttl">
                      {content?.thinkingType || `A 論理タイプ`}
                    </h4>
                  </hgroup>

                  {/* レーダーチャートエリア */}
                  <div className="c-report-single_result-1_main_radar js-radar-chart-val">
                    <div className="c-report-single_result-1_main_radar_chart">
                      <DesignRadarChart
                        scoreA={scores.A}
                        scoreB={scores.B}
                        scoreC={scores.C}
                        scoreD={scores.D}
                      />
                    </div>

                    {/* A 論理 - 左上 */}
                    <div className="c-report-single_result-1_main_radar_box _pos-a">
                      <h5 className="c-report-single_result-1_main_radar_box_ttl">
                        <span className="c-report-single_result-1_main_radar_box_ttl_val">
                          <span className="c-report-single_result-1_main_radar_box_ttl_val_lg js-radar-chart-val-a">
                            {scores.A}
                          </span>/12
                        </span>
                        <span className="c-report-single_result-1_main_radar_box_ttl_txt">
                          <span className="c-report-single_result-1_main_radar_box_ttl_txt_lg">A</span>
                          論理
                        </span>
                      </h5>
                      <dl className="c-report-single_result-1_main_radar_box_dl">
                        <div className="c-report-single_result-1_main_radar_box_dl_inner">
                          <dt className="c-report-single_result-1_main_radar_box_dl_theme">
                            A 論理タイプの特徴
                          </dt>
                          <dd className="c-report-single_result-1_main_radar_box_dl_des">
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_disc">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                データに基づき合理的に考える
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                情報を整理し結論を導く
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                曖昧さを嫌い明確さを求める
                              </li>
                            </ul>
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_box">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">事実重視</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">数量的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">理性的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">論理的</li>
                            </ul>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* B 秩序 - 左下 */}
                    <div className="c-report-single_result-1_main_radar_box _pos-b">
                      <h5 className="c-report-single_result-1_main_radar_box_ttl">
                        <span className="c-report-single_result-1_main_radar_box_ttl_val">
                          <span className="c-report-single_result-1_main_radar_box_ttl_val_lg js-radar-chart-val-b">
                            {scores.B}
                          </span>/12
                        </span>
                        <span className="c-report-single_result-1_main_radar_box_ttl_txt">
                          <span className="c-report-single_result-1_main_radar_box_ttl_txt_lg">B</span>
                          秩序
                        </span>
                      </h5>
                      <dl className="c-report-single_result-1_main_radar_box_dl">
                        <div className="c-report-single_result-1_main_radar_box_dl_inner">
                          <dt className="c-report-single_result-1_main_radar_box_dl_theme">
                            B 秩序タイプの特徴
                          </dt>
                          <dd className="c-report-single_result-1_main_radar_box_dl_des">
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_disc">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                効率重視で実務を遂行
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                計画性と管理力に長ける
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                秩序を守り冷静に行動
                              </li>
                            </ul>
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_box">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">保守・形式的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">実直</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">感情抑制</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">順序立つ</li>
                            </ul>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* C 共感 - 右下 */}
                    <div className="c-report-single_result-1_main_radar_box _pos-c">
                      <h5 className="c-report-single_result-1_main_radar_box_ttl">
                        <span className="c-report-single_result-1_main_radar_box_ttl_txt">
                          <span className="c-report-single_result-1_main_radar_box_ttl_txt_lg">C</span>
                          共感
                        </span>
                        <span className="c-report-single_result-1_main_radar_box_ttl_val">
                          <span className="c-report-single_result-1_main_radar_box_ttl_val_lg js-radar-chart-val-c">
                            {scores.C}
                          </span>/12
                        </span>
                      </h5>
                      <dl className="c-report-single_result-1_main_radar_box_dl">
                        <div className="c-report-single_result-1_main_radar_box_dl_inner">
                          <dt className="c-report-single_result-1_main_radar_box_dl_theme">
                            C 共感タイプの特徴
                          </dt>
                          <dd className="c-report-single_result-1_main_radar_box_dl_des">
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_disc">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                人との関わりや表現が得意
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                気配りでチームを支える
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                感性豊かで共感力が高い
                              </li>
                            </ul>
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_box">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">感覚·感情的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">音楽的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">直感的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">話好き</li>
                            </ul>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* D 創造 - 右上 */}
                    <div className="c-report-single_result-1_main_radar_box _pos-d">
                      <h5 className="c-report-single_result-1_main_radar_box_ttl">
                        <span className="c-report-single_result-1_main_radar_box_ttl_txt">
                          <span className="c-report-single_result-1_main_radar_box_ttl_txt_lg">D</span>
                          創造
                        </span>
                        <span className="c-report-single_result-1_main_radar_box_ttl_val">
                          <span className="c-report-single_result-1_main_radar_box_ttl_val_lg js-radar-chart-val-d">
                            {scores.D}
                          </span>/12
                        </span>
                      </h5>
                      <dl className="c-report-single_result-1_main_radar_box_dl">
                        <div className="c-report-single_result-1_main_radar_box_dl_inner">
                          <dt className="c-report-single_result-1_main_radar_box_dl_theme">
                            D 創造タイプの特徴
                          </dt>
                          <dd className="c-report-single_result-1_main_radar_box_dl_des">
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_disc">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                広い視野で発想が豊か
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                独自の発想で新しい可能性を生む
                              </li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_disc_item">
                                変化を楽しみ、挑戦に前向き
                              </li>
                            </ul>
                            <ul className="c-report-single_result-1_main_radar_box_dl_des_box">
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">想像力豊か</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">芸術的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">空間的</li>
                              <li className="c-report-single_result-1_main_radar_box_dl_des_box_item">未来志向</li>
                            </ul>
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>

                {/* サブセクション */}
                {content && (
                  <div className="c-report-single_result-1_sub">
                    <hgroup className="c-report-single_result-1_sub_hgroup">
                      <p className="c-report-single_result-1_sub_hgroup_txt">あなたは</p>
                      <h4 className="c-report-single_result-1_sub_hgroup_ttl">
                        {content.temperamentType}
                      </h4>
                    </hgroup>
                    <dl className="c-report-single_result-1_sub_dl">
                      <div className="c-report-single_result-1_sub_dl_item">
                        <dt className="c-report-single_result-1_sub_dl_style _thought">思考スタイル</dt>
                        <dt className="c-report-single_result-1_sub_dl_theme">{content.thoughtTheme}</dt>
                        <dd className="c-report-single_result-1_sub_dl_des">{content.thoughtDescription}</dd>
                      </div>
                      <div className="c-report-single_result-1_sub_dl_item">
                        <dt className="c-report-single_result-1_sub_dl_style _action">業務上の行動スタイル</dt>
                        <dt className="c-report-single_result-1_sub_dl_theme">{content.actionTheme}</dt>
                        <dd className="c-report-single_result-1_sub_dl_des">{content.actionDescription}</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>

              {/* フッター */}
              <footer className="c-report-single_footer">
                <p className="c-report-single_footer_copy">
                  <small>Copyright &copy; GloBrace,Inc. All Rights Reserved.</small>
                </p>
              </footer>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DesignReportView;
